import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Container,
  MenuItem,
  Drawer,
  Menu,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";
import SiteIcon from "./Icon";
import { routeKeys, RouteItem } from "../routes";
import { useTranslation } from "react-i18next";
import "flag-icons/css/flag-icons.min.css";

/* ---------------- styled ---------------- */

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
  borderRadius: `calc(${theme.shape.borderRadius}px + 8px)`,
  backdropFilter: "blur(24px)",
  border: "1px solid",
  borderColor: (theme.vars || theme).palette.divider,
  backgroundColor: theme.vars
    ? `rgba(${theme.vars.palette.background.defaultChannel} / 0.4)`
    : alpha(theme.palette.background.default, 0.4),
  boxShadow: (theme.vars || theme).shadows[1],
  padding: "8px 12px",
}));

/* ---------------- helpers ---------------- */

function LanguageFlag({ lang }: { lang: string }) {
  return (
    <span
      className={`fi fi-${lang.startsWith("en") ? "us" : "jp"}`}
      style={{ fontSize: "1.2em" }}
    />
  );
}

function NavLinks({
  variant,
  onClick,
}: {
  variant: "button" | "menu";
  onClick?: () => void;
}) {
  const { t } = useTranslation();
  const [trainingAnchor, setTrainingAnchor] =
    React.useState<null | HTMLElement>(null);
  const trainingMenuOpen = Boolean(trainingAnchor);

  const openTrainingMenu = (e: React.MouseEvent<HTMLElement>) =>
    setTrainingAnchor(e.currentTarget);

  const closeTrainingMenu = () => setTrainingAnchor(null);

  return (
    <>
      {routeKeys.map((r) => {
        if ("children" in r && r.children?.length) {
          if (variant === "button") {
            return (
              <React.Fragment key={r.key}>
                <Button
                  variant="text"
                  color="info"
                  onClick={openTrainingMenu}
                  endIcon={<ArrowDropDownIcon />}
                  sx={{ mx: 0.5 }}
                >
                  {t(`nav.${r.key}`)}
                </Button>
                <Menu
                  anchorEl={trainingAnchor}
                  open={trainingMenuOpen}
                  onClose={closeTrainingMenu}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  {r.children.map((child) => (
                    <MenuItem
                      key={child.key}
                      component="a"
                      href={child.route}
                      onClick={() => {
                        closeTrainingMenu();
                        if (onClick) onClick();
                      }}
                    >
                      {t(`nav.${child.key}`)}
                    </MenuItem>
                  ))}
                </Menu>
              </React.Fragment>
            );
          }

          return (
            <Box key={r.key} sx={{ width: "100%" }}>
              <MenuItem disabled sx={{ opacity: 1, fontWeight: "bold" }}>
                {t(`nav.${r.key}`)}
              </MenuItem>
              {r.children.map((child) => (
                <MenuItem
                  key={child.key}
                  component="a"
                  href={child.route}
                  onClick={onClick}
                  sx={{ pl: 4 }}
                >
                  {t(`nav.${child.key}`)}
                </MenuItem>
              ))}
            </Box>
          );
        } else {
          const item = r as RouteItem;
          return variant === "button" ? (
            <Button
              key={item.key}
              variant="text"
              color="info"
              href={item.route}
              target={item.component === null ? "_blank" : undefined}
              rel={item.component === null ? "noopener noreferrer" : undefined}
              sx={{ mx: 0.5 }}
            >
              {t(`nav.${item.key}`)}
            </Button>
          ) : (
            <MenuItem
              key={item.key}
              component="a"
              href={item.route}
              target={item.component === null ? "_blank" : undefined}
              rel={item.component === null ? "noopener noreferrer" : undefined}
              onClick={onClick}
            >
              {t(`nav.${item.key}`)}
            </MenuItem>
          );
        }
      })}
    </>
  );
}

/* ---------------- component ---------------- */

export default function AppAppBar() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [langAnchor, setLangAnchor] = React.useState<null | HTMLElement>(null);

  const { i18n } = useTranslation();
  const langMenuOpen = Boolean(langAnchor);

  const toggleDrawer = (open: boolean) => () => setDrawerOpen(open);

  const openLanguageMenu = (e: React.MouseEvent<HTMLElement>) =>
    setLangAnchor(e.currentTarget);

  const closeLanguageMenu = () => setLangAnchor(null);

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    closeLanguageMenu();
  };

  return (
    <AppBar
      position="fixed"
      enableColorOnDark
      sx={{
        boxShadow: 0,
        bgcolor: "transparent",
        backgroundImage: "none",
        mt: "calc(var(--template-frame-height, 0px) + 28px)",
      }}
    >
      <Container maxWidth="lg">
        <StyledToolbar variant="dense" disableGutters>
          {/* ---------- left ---------- */}
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <a href="/">
              <SiteIcon />
            </a>

            <Box sx={{ display: { xs: "none", md: "flex" } }}>
              <NavLinks variant="button" />
            </Box>
          </Box>

          {/* ---------- right / desktop ---------- */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 1,
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={openLanguageMenu}
              aria-label="language"
              aria-haspopup="true"
              aria-expanded={langMenuOpen}
              title={i18n.language === "en" ? "English" : "日本語"}
            >
              <LanguageFlag lang={i18n.language} />
            </IconButton>

            <ColorModeIconDropdown />
          </Box>

          {/* ---------- right / mobile ---------- */}
          <Box sx={{ display: { xs: "flex", md: "none" }, gap: 1 }}>
            <IconButton
              onClick={openLanguageMenu}
              aria-label="language"
              aria-haspopup="true"
              aria-expanded={langMenuOpen}
            >
              <LanguageFlag lang={i18n.language} />
            </IconButton>

            <ColorModeIconDropdown />

            <IconButton aria-label="menu" onClick={toggleDrawer(true)}>
              <MenuIcon />
            </IconButton>
          </Box>
        </StyledToolbar>
      </Container>

      {/* ---------- language menu (shared) ---------- */}
      <Menu
        anchorEl={langAnchor}
        open={langMenuOpen}
        onClose={closeLanguageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          selected={i18n.language === "en"}
          onClick={() => changeLanguage("en")}
        >
          <span className="fi fi-us" style={{ marginRight: 8 }} />
          English
        </MenuItem>
        <MenuItem
          selected={i18n.language === "ja"}
          onClick={() => changeLanguage("ja")}
        >
          <span className="fi fi-jp" style={{ marginRight: 8 }} />
          日本語
        </MenuItem>
      </Menu>

      {/* ---------- mobile drawer ---------- */}
      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { top: "var(--template-frame-height, 0px)" },
        }}
      >
        <Box sx={{ p: 2, bgcolor: "background.default" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={toggleDrawer(false)}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>

          <NavLinks variant="menu" onClick={toggleDrawer(false)} />
        </Box>
      </Drawer>
    </AppBar>
  );
}
